import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Avatar } from 'base/ui/avatar';
import { Button } from 'base/ui/button';
import { DndDomain } from 'base/ui/dnd';
import { IconDragHorizontally, IconDragVertically } from 'base/ui/icons';
import { globalResizeObserver } from 'base/ui/measure';
import { ServerPresenceJoinMessage } from 'blocks/blocks_service';
import {
  canReorderInSlot,
  getParentSlotOfElement,
} from 'blocks/component_renderer/slots';
import { elementDndDomain } from 'blocks/component_renderer/slots_dnd';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import classNames from 'classnames';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { unrefSection } from '../model_helpers';
import styles from './frame.css';
import { getLinkedStates } from './linked_states';
import {
  globalSelectionsMeasurer,
  SELECTABLE_ORIGINAL_ID_ATTR,
  SELECTABLE_OR_FRAME_ID_ATTR,
} from './measurer';
import { idForSelectionState, SelectionState } from './states';
import { SelectionStore } from './store';

type FrameColor = 'global' | 'ai';

export type FrameOpts = Readonly<{
  frameColor?: FrameColor;
  dragHandle?: DragHandleOpts;
  hideFrame?: boolean;
  /* Key is a react key for the action */
  actions?: Record<string, SelectionFrameAction | undefined>;
  disableHoverExtension?: boolean;
}>;

export type SelectionFrameAction = {
  pos: ActionPosition;
  content: React.ReactNode;
  showWhenHovering: boolean;
  showWhenSelected: boolean;
  maxWidthRatio?: number;
  maxHeightRatio?: number;
};

export type DragHandleOpts = {
  domain: DndDomain<any, any>;
  draggablePosition: 'parent' | 'child';
  handlePosition: 'left' | 'bottom' | 'top';
  isParent?: boolean;
  onClick?: () => void;
};

/**
 * Stores a reference to the default frame options, for
 * all selectables that are currently mounted.
 **/
class SelectionFrameRegistry {
  readonly byId = observable.map<string, FrameOpts>([], { deep: false });

  register(targetId: string, defaultFrame: FrameOpts) {
    this.byId.set(targetId, defaultFrame);
    return () => {
      if (this.byId.get(targetId) === defaultFrame) {
        this.byId.delete(targetId);
      }
    };
  }
}

export const globalSelectionFrameRegistry = new SelectionFrameRegistry();

class SelectionHoverRegistry {
  @observable.ref
  hoveringId = '';

  private idToStateRefCount = new Map<string, number>();
  private readonly idToState = observable.map<string, SelectionState>([], {
    deep: false,
  });

  constructor() {
    makeObservable(this);
  }

  @computed
  get hoveringState() {
    return this.idToState.get(this.hoveringId);
  }

  attach(state: SelectionState, el: HTMLElement, mode: 'original' | 'related') {
    const id = idForSelectionState(state);
    el.setAttribute(SELECTABLE_OR_FRAME_ID_ATTR, id);
    if (mode === 'original') el.setAttribute(SELECTABLE_ORIGINAL_ID_ATTR, id);
    this.incrementStateRefCount(id, state);
    return () => {
      if (el.getAttribute(SELECTABLE_OR_FRAME_ID_ATTR) === id) {
        el.removeAttribute(SELECTABLE_OR_FRAME_ID_ATTR);
      }
      if (
        mode === 'original' &&
        el.getAttribute(SELECTABLE_ORIGINAL_ID_ATTR) === id
      ) {
        el.removeAttribute(SELECTABLE_ORIGINAL_ID_ATTR);
      }
      this.decrementStateRefCount(id);
    };
  }

  private incrementStateRefCount(id: string, state: SelectionState) {
    const refCount = (this.idToStateRefCount.get(id) || 0) + 1;
    this.idToStateRefCount.set(id, refCount);
    if (!this.idToState.has(id)) {
      this.idToState.set(id, state);
    }
  }

  private decrementStateRefCount(id: string) {
    const refCount = (this.idToStateRefCount.get(id) || 0) - 1;
    this.idToStateRefCount.set(id, refCount);
    if (refCount <= 0) {
      this.idToState.delete(id);
    }
  }

  listenForHover() {
    // We can't use CSS :hover, as selectables might be nested and we only
    // want to display the hover state on the innermost one
    const onMouseMove = (event: MouseEvent) => {
      if (!(event.target instanceof HTMLElement)) return;
      const el = event.target;
      let hoveringId;
      if (!el.hasAttribute(SELECTION_HOVER_THROUGH_ATTR)) {
        // Fast path - using el.target
        const closestSelectable = el.closest(
          `[${SELECTABLE_OR_FRAME_ID_ATTR}]`,
        );
        hoveringId =
          (closestSelectable instanceof HTMLElement &&
            closestSelectable.getAttribute(SELECTABLE_OR_FRAME_ID_ATTR)) ||
          '';
      } else {
        // Slow path - another hit test
        hoveringId =
          this.getSelectableIdAtCursor(event.clientX, event.clientY) || '';
      }
      if (this.hoveringId !== hoveringId) {
        this.hoveringId = hoveringId;
      }
    };
    const onDragStart = () => {
      // Hide the hover state when dragging starts:
      this.hoveringId = '';
      // The browser doesn't fire mousemove events while dragging,
      // so we don't need to disable that ourselves.
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('dragstart', onDragStart, { capture: true });
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('dragstart', onDragStart, { capture: true });
    };
  }

  private getSelectableIdAtCursor(x: number, y: number) {
    const hitTest = document.elementsFromPoint(x, y);
    for (const el of hitTest) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.hasAttribute(SELECTION_HOVER_THROUGH_ATTR)) continue;
      const closestSelectable = el.closest(`[${SELECTABLE_OR_FRAME_ID_ATTR}]`);
      const id =
        closestSelectable instanceof HTMLElement &&
        closestSelectable.getAttribute(SELECTABLE_OR_FRAME_ID_ATTR);
      if (id) {
        return id;
      }
    }
    return;
  }

  getSelectableAtCursor(
    x: number,
    y: number,
  ): { state: SelectionState; id: string } | undefined {
    const id = this.getSelectableIdAtCursor(x, y);
    if (!id) return;
    const state = this.idToState.get(id);
    if (state) return { id, state };
  }
}

export const globalSelectionHoverRegistry = new SelectionHoverRegistry();

/* Ignore these elements when considering what is selected / hovered */
export const SELECTION_HOVER_THROUGH_ATTR = 'data-rlselectionhoverthrough';

type DragHandleLayout = {
  top: number;
  left: number;
  width: number;
  height: number;
};

class DragHandleRegistry {
  private readonly byId = observable.map<string, DragHandleLayout>([], {
    deep: false,
  });

  get(targetId: string) {
    return this.byId.get(targetId);
  }

  update(targetId: string, dragHandle: DragHandleLayout) {
    this.byId.set(targetId, dragHandle);
  }

  remove(targetId: string) {
    this.byId.delete(targetId);
  }
}

const globalDragHandleRegistry = new DragHandleRegistry();

export const SelectionFrames = observer(function _SelectionFrames({
  store,
  flags,
  library,
}: {
  store: SelectionStore;
  flags: FeatureFlagsStore;
  library: LibraryCache;
}) {
  React.useEffect(() => globalSelectionHoverRegistry.listenForHover(), []);

  const selectedId = store.getId();
  const { hoveringId, hoveringState } = globalSelectionHoverRegistry;
  const linkedToSelected = getLinkedStates(store.get(), store).map(
    idForSelectionState,
  );
  const otherUsers = store.decorations.getOtherSelections();
  const linkedToHovering = getLinkedStates(hoveringState, store).map(
    idForSelectionState,
  );

  const selection = store.get();
  const selectionParent = selection.element
    ? store.factory.parentOfElementNode(selection, library)
    : undefined;

  const selectedParentId = selectionParent
    ? idForSelectionState(selectionParent)
    : undefined;

  let canReorder = false;
  let canParentReorder = false;

  // Find the slot the selected element is in.
  let selectedSiblingsCount: number | undefined;
  if (selection.element) {
    const section = unrefSection(selection.section, store.project);
    const rootElement = section?.element.current;
    if (rootElement) {
      const slot = getParentSlotOfElement({
        rootElement,
        element: selection.element,
        library,
      });

      if (slot) {
        selectedSiblingsCount = slot.override.items.length;

        canReorder = canReorderInSlot(slot.type);
      }

      if (selectionParent?.element) {
        const parentSlot = getParentSlotOfElement({
          rootElement,
          element: selectionParent.element,
          library,
        });

        if (parentSlot) {
          canParentReorder = canReorderInSlot(parentSlot.type);
        }
      } else if (selectionParent?.section) {
        canParentReorder = true;
      }
    }
  } else if (selection.section) {
    canReorder = true;
  }

  const allTargets = [
    ...otherUsers.keys(),
    ...linkedToSelected,
    ...linkedToHovering,
    ...(hoveringId ? [hoveringId] : []),
    // Only show selection parent frame for elements, not sections.
    ...(selectedParentId && selectionParent?.element ? [selectedParentId] : []),
    ...(selectedId ? [selectedId] : []),
  ];

  let doDragHandlesOverlap = false;
  if (selectedId && selectedParentId) {
    const childHandle = globalDragHandleRegistry.get(selectedId);
    const parentHandle = globalDragHandleRegistry.get(selectedParentId);

    if (childHandle && parentHandle) {
      const childX = childHandle.left;
      const childY = childHandle.top;
      const parentX = parentHandle.left;
      const parentY = parentHandle.top;
      doDragHandlesOverlap =
        childX < parentX + 24 &&
        childX + 24 > parentX &&
        childY < parentY + 24 &&
        childY + 24 > parentY;
    }
  }

  let allFrames: React.ReactNode[] = [];
  let allActions: React.ReactNode[] = [];
  for (const targetId of allTargets) {
    const defaultFrame = globalSelectionFrameRegistry.byId.get(targetId);
    if (!defaultFrame) continue;
    const {
      actions,
      frameColor,
      dragHandle,
      hideFrame,
      disableHoverExtension,
    } = defaultFrame;

    const otherUser = otherUsers.get(targetId);
    const isSelected = selectedId === targetId;
    const isSelectedParent =
      selectedParentId === targetId && selectionParent?.element !== undefined;
    const isHovering = hoveringId === targetId;
    const isLinkedToSelected = linkedToSelected.includes(targetId);
    const weightClass = isSelected
      ? styles.weightSelected
      : isHovering
      ? styles.weightHovering
      : isSelectedParent
      ? styles.dashedBorder
      : isLinkedToSelected
      ? styles.weightLinkedToSelection
      : styles.weightLinkedToHovering;

    allFrames.push(
      fade(
        `${targetId}-frame`,
        <SelectionFrame
          id={targetId}
          weightClass={weightClass}
          color={frameColor || 'ai'}
          otherUser={otherUser}
          // Prefer our own color if we're selecting it too:
          displayOtherUserColor={!isSelected}
          dragHandle={
            isSelected && selectedSiblingsCount !== 1
              ? dragHandle
              : isSelectedParent && selectionParent?.element && dragHandle
              ? {
                  ...dragHandle,
                  isParent: true,
                  onClick: () => {
                    if (selection.element !== undefined) {
                      store.set(
                        store.factory.parentOfElementNode(selection, library),
                      );
                    }
                  },
                }
              : undefined
          }
          hideFrame={hideFrame}
          // Add a small margin around the parent's selection border so they don't overlap.
          margin={isSelectedParent ? 1 : 0}
          // If drag handles overlap, hide the parent handle.
          hideDragHandle={
            (isSelectedParent && doDragHandlesOverlap) ||
            !(isSelectedParent ? canParentReorder : canReorder)
          }
        />,
      ),
    );
    for (const [key, action] of Object.entries(actions || {})) {
      if (
        action?.content &&
        ((isSelected && action.showWhenSelected) ||
          (isHovering && action.showWhenHovering))
      ) {
        allActions.push(
          fade(
            `${targetId}-action-${key}`,
            <SelectionAction
              id={targetId}
              content={action.content}
              pos={action.pos}
              enableHoverExtension={isHovering && !disableHoverExtension}
              maxWidthRatio={action.maxWidthRatio}
              maxHeightRatio={action.maxHeightRatio}
            />,
          ),
        );
      }
    }
  }

  const dndState = elementDndDomain.state.get();
  if (dndState) return null;

  return (
    <TransitionGroup>
      {allFrames}
      {allActions}
    </TransitionGroup>
  );
});

function fade(key: string, node: React.ReactNode) {
  return (
    <CSSTransition key={key} timeout={200} classNames={styles}>
      <div className={styles.hasFade}>{node}</div>
    </CSSTransition>
  );
}

const FADE_PROPS = {
  timeout: 150,
  classNames: styles,
  appear: true,
  mountOnEnter: true,
  unmountOnExit: true,
};

const SelectionFrame = observer(function _SelectionFrame({
  id,
  weightClass,
  color,
  otherUser,
  displayOtherUserColor,
  dragHandle,
  hideFrame,
  margin = 0,
  hideDragHandle,
}: {
  id: string;
  weightClass: string;
  color: FrameColor;
  otherUser?: ServerPresenceJoinMessage;
  displayOtherUserColor: boolean;
  dragHandle?: DragHandleOpts;
  hideFrame?: boolean;
  margin?: number;
  hideDragHandle?: boolean;
}) {
  const frameRef = React.useRef<HTMLDivElement>(null);
  const handleRef = React.useRef<HTMLDivElement>(null);
  const avatarRef = React.useRef<HTMLDivElement>(null);

  // Cache the position so that the handle doesn't rotate when the drag
  // feature is transitioning in and out.
  const handlePositionRef = React.useRef(dragHandle?.handlePosition ?? 'left');
  if (dragHandle) handlePositionRef.current = dragHandle.handlePosition;
  const handlePosition = handlePositionRef.current;

  const measurer = globalSelectionsMeasurer.get('frame', id);

  const draggableId = React.useMemo(
    () =>
      measurer.el
        ? dragHandle?.domain.getDraggableId(
            measurer.el,
            dragHandle.draggablePosition,
          )
        : undefined,
    [measurer.el, dragHandle],
  );

  React.useEffect(
    () =>
      reaction(
        () => measurer.rect,
        (rect) => {
          const frame = frameRef.current;
          const handle = handleRef.current;
          const avatar = avatarRef.current;
          if (!frame) return;

          if (!measurer.el || rect.width === 0 || rect.height === 0) {
            frame.style.visibility = 'hidden';
            handle && (handle.style.visibility = 'hidden');
            avatar && (avatar.style.visibility = 'hidden');
            return;
          }

          frame.style.visibility = 'visible';
          // We don't use react as it delays the updates when panning & zooming.
          frame.style.top = rect.top - margin + 'px';
          frame.style.left = rect.left - margin + 'px';
          frame.style.width = rect.width + margin * 2 + 'px';
          frame.style.height = rect.height + margin * 2 + 'px';

          const style = window.getComputedStyle(measurer.el);
          const scale = rect.width / parseFloat(style.width);
          frame.style.borderTopLeftRadius =
            parseFloat(style.borderTopLeftRadius) * scale + 'px';
          frame.style.borderTopRightRadius =
            parseFloat(style.borderTopRightRadius) * scale + 'px';
          frame.style.borderBottomLeftRadius =
            parseFloat(style.borderBottomLeftRadius) * scale + 'px';
          frame.style.borderBottomRightRadius =
            parseFloat(style.borderBottomRightRadius) * scale + 'px';

          let handleTop: number;
          let handleLeft: number;

          if (handle != null) {
            switch (handlePosition) {
              case 'left':
                handleTop = rect.top + rect.height / 2;
                handleLeft = rect.left - handle.offsetWidth;
                handle.style.transform = 'translateY(-50%)';
                break;
              case 'bottom':
                handleTop = rect.top + rect.height;
                handleLeft = rect.left + rect.width / 2;
                handle.style.transform = 'translateX(-50%)';
                break;
              case 'top':
                handleTop = rect.top - handle.offsetHeight;
                handleLeft = rect.left + rect.width / 2;
                handle.style.transform = 'translateX(-50%)';
                break;
              default:
                throw new UnreachableError(handlePosition);
            }

            handle.style.visibility = hideDragHandle ? 'hidden' : 'visible';
            handle.style.top = handleTop + 'px';
            handle.style.left = handleLeft + 'px';

            const widthRatio = handle.offsetWidth / rect.width;
            const heightRatio = handle.offsetHeight / rect.height;

            if (
              ((handlePosition === 'top' || handlePosition === 'bottom') &&
                widthRatio > 1) ||
              (handlePosition === 'left' && heightRatio > 2)
            ) {
              handle.style.visibility = 'hidden';
              globalDragHandleRegistry.remove(id);
            } else {
              globalDragHandleRegistry.update(id, {
                top: handleTop,
                left: handleLeft,
                width: handle.offsetWidth,
                height: handle.offsetHeight,
              });
            }
          }

          if (avatar != null) {
            avatar.style.visibility = otherUser ? 'visible' : 'hidden';
            avatar.style.top = rect.top + rect.height / 2 + 'px';
            avatar.style.left = rect.left - avatar.offsetWidth + 'px';
            avatar.style.transform = `${
              draggableId &&
              handle?.style.visibility === 'visible' &&
              handlePosition === 'left'
                ? 'translateX(-28px)'
                : ''
            } translateY(-50%)`;
          }
        },
        { fireImmediately: true },
      ),
    [
      measurer,
      draggableId,
      handlePosition,
      hideDragHandle,
      margin,
      id,
      otherUser,
    ],
  );

  React.useEffect(() => () => globalDragHandleRegistry.remove(id), [id]);

  const handleIn = dragHandle != null && draggableId != null;
  const handleButton = (
    <div
      className={classNames(
        styles.handle,
        styles[`position-${handlePosition}`],
      )}
    >
      {dragHandle?.isParent ? (
        <Button
          icon={
            handlePosition === 'left'
              ? IconDragVertically
              : IconDragHorizontally
          }
          variant="secondary"
          background={color === 'global' ? 'lightGreen' : 'lightPurple'}
          iconColor={color === 'global' ? 'green' : 'lightPurple'}
          size="small"
        />
      ) : (
        <Button
          icon={
            handlePosition === 'left'
              ? IconDragVertically
              : IconDragHorizontally
          }
          variant="primary"
          background={color}
          size="small"
        />
      )}
    </div>
  );
  return (
    <>
      <div
        className={classNames(
          styles.frame,
          weightClass,
          color === 'global' && styles.isGlobal,
        )}
        ref={frameRef}
        style={{
          ['--frameColor' as string]:
            otherUser && displayOtherUserColor
              ? otherUser.color
              : hideFrame
              ? 'transparent'
              : undefined,
        }}
      />
      <CSSTransition {...FADE_PROPS} nodeRef={avatarRef} in={otherUser != null}>
        <div
          ref={avatarRef}
          className={classNames(styles.avatar, styles.hasFade)}
          {...{ [SELECTABLE_OR_FRAME_ID_ATTR]: id }}
        >
          <Avatar
            {...(otherUser?.user ?? {
              color: otherUser?.color,
              isAnonymous: true,
            })}
            size="small"
          />
        </div>
      </CSSTransition>
      <CSSTransition {...FADE_PROPS} nodeRef={handleRef} in={handleIn}>
        <div
          ref={handleRef}
          className={classNames(styles.handleContainer, styles.hasFade)}
          // make this focusable so that selection is maintained when clicking on it
          tabIndex={0}
          {...{ [SELECTABLE_OR_FRAME_ID_ATTR]: id }}
        >
          {dragHandle != null && draggableId != null ? (
            <dragHandle.domain.DragHandle
              draggableId={draggableId}
              onClick={dragHandle.onClick}
            >
              {handleButton}
            </dragHandle.domain.DragHandle>
          ) : (
            // used when transitioning in and out
            handleButton
          )}
        </div>
      </CSSTransition>
    </>
  );
});

type ActionPosition = {
  fixedScale?: number;
  contentHeightFactor: number;
  targetTopFactor: number;
  targetBottomFactor: number;
  yAdjustScaledPx: number;
  yAdjustStaticPx: number;
  contentWidthFactor: number;
  targetWidthFactor: number;
};

/* top (above the line), left */
export const ASK_AI_ACTION_POSITION: ActionPosition = {
  fixedScale: 1,
  contentHeightFactor: 0,
  targetTopFactor: 1,
  targetBottomFactor: 0,
  yAdjustScaledPx: -1.5, // for border
  yAdjustStaticPx: -2.5, // some spacing
  contentWidthFactor: 0,
  targetWidthFactor: 0,
};

/** on the line, in the middle of their respective sides */
export const ADD_LEFT_ACTION_POSITION: ActionPosition = {
  fixedScale: 1,
  contentHeightFactor: -0.5,
  targetTopFactor: 0.5,
  targetBottomFactor: 0.5,
  yAdjustScaledPx: 0,
  yAdjustStaticPx: 0,
  contentWidthFactor: -0.5,
  targetWidthFactor: 0,
};
export const ADD_RIGHT_ACTION_POSITION: ActionPosition = {
  fixedScale: 1,
  contentHeightFactor: -0.5,
  targetTopFactor: 0.5,
  targetBottomFactor: 0.5,
  yAdjustScaledPx: 0,
  yAdjustStaticPx: 0,
  contentWidthFactor: -0.5,
  targetWidthFactor: 1,
};
export const ADD_BELOW_ACTION_POSITION: ActionPosition = {
  fixedScale: 1,
  contentHeightFactor: -0.5,
  targetTopFactor: 0,
  targetBottomFactor: 1,
  yAdjustScaledPx: 0,
  yAdjustStaticPx: 0,
  contentWidthFactor: -0.5,
  targetWidthFactor: 0.5,
};
export const ADD_ABOVE_ACTION_POSITION: ActionPosition = {
  fixedScale: 1,
  contentHeightFactor: -0.5,
  targetTopFactor: 1,
  targetBottomFactor: 0,
  yAdjustScaledPx: 0,
  yAdjustStaticPx: 0,
  contentWidthFactor: -0.5,
  targetWidthFactor: 0.5,
};

const SelectionAction = observer(function _SelectionAction({
  id,
  content,
  pos,
  enableHoverExtension,
  maxWidthRatio,
  maxHeightRatio,
}: {
  id: string;
  content: React.ReactNode;
  pos: ActionPosition;
  enableHoverExtension: boolean;
  maxWidthRatio?: number;
  maxHeightRatio?: number;
}) {
  const hoverExtensionRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const measurer = globalSelectionsMeasurer.get('frame', id);

  const reposition = React.useCallback(() => {
    const rect = measurer.rect;
    if (!measurer.el || rect.width === 0 || rect.height === 0) {
      if (hoverExtensionRef.current) {
        hoverExtensionRef.current.style.display = 'none';
      }
      if (contentRef.current) {
        contentRef.current.style.visibility = 'hidden';
      }
      return;
    }

    const scale =
      pos.fixedScale ||
      rect.width / parseFloat(window.getComputedStyle(measurer.el).width);

    const content = contentRef.current;
    if (!content) return;
    const { offsetWidth, offsetHeight } = content;
    content.style.visibility = 'visible';
    const contentTop =
      rect.top * pos.targetTopFactor +
      rect.bottom * pos.targetBottomFactor +
      pos.yAdjustScaledPx * scale +
      pos.yAdjustStaticPx +
      offsetHeight * scale * pos.contentHeightFactor;
    // When `targetBottomFactor` is 0 it means that the `SelectionAction` must be display
    // above the selection frame.
    // We used to use `top` to position the `SelectionAction` vertically but it was
    // problematic because we would need 2 renders to position the `SelectionAction` properly:
    // * one to first render the `SelectionAction` and _measure_ the component,
    // * and a second one to reposition the SelectionAction taking into consideration
    //   the height of the rendered component.
    // The issue is that if you double click, it's possible to interact with the
    // `SelectionAction` on the first render which is inside the selection frame
    // (which isn't it's final position), and that's unexpected because the
    // the target of the double click is for the text element inside the selection frame.
    // More info in this bug report: https://www.notion.so/relume/Bug-with-Text-Editing-1825c3c8064a80138e56cd444ef3d7e1?pvs=4
    // Using the `style.bottom` fixes the issue because then we don't need to know
    // the `height` of the `SelectionAction` to position it above the selection frame.
    if (pos.targetBottomFactor === 0) {
      const contentBottom =
        window.innerHeight -
        rect.top -
        pos.yAdjustScaledPx * scale -
        pos.yAdjustStaticPx +
        offsetHeight * pos.contentHeightFactor;
      content.style.top = '';
      content.style.bottom = `${contentBottom}px`;
    } else {
      content.style.top = `${contentTop}px`;
      content.style.bottom = '';
    }
    const contentLeft =
      rect.left +
      rect.width * pos.targetWidthFactor +
      offsetWidth * scale * pos.contentWidthFactor;
    content.style.left = `${contentLeft}px`;
    content.style.transform = `scale(${scale})`;

    if (maxWidthRatio) {
      const widthRatio = offsetWidth / rect.width;
      if (widthRatio > maxWidthRatio) {
        content.style.visibility = 'hidden';
      }
    }
    if (maxHeightRatio) {
      const heightRatio = offsetHeight / rect.height;
      if (heightRatio > maxHeightRatio) {
        content.style.visibility = 'hidden';
      }
    }

    const hext = hoverExtensionRef.current;
    if (!hext) return;
    hext.style.display = 'block';
    hext.style.left = rect.left + 'px';
    hext.style.width = rect.width + 'px';
    if (contentTop <= rect.top) {
      hext.style.top = contentTop + 'px';
      hext.style.height = rect.top - contentTop + 'px';
    } else {
      hext.style.top = rect.bottom + 'px';
      hext.style.height =
        contentTop + offsetHeight * scale - rect.bottom + 'px';
    }
  }, [measurer, pos, maxHeightRatio, maxWidthRatio]);

  React.useEffect(
    () => reaction(() => measurer.rect, reposition, { fireImmediately: true }),
    [measurer, reposition],
  );
  React.useEffect(
    () =>
      contentRef.current
        ? globalResizeObserver.observe(contentRef.current, reposition)
        : undefined,
    [reposition],
  );

  return (
    <>
      {enableHoverExtension && (
        /**
         * This is how it works for bottom actions, but top is similar:
         * This fills the areas denoted with "x":
         *
           ┌────────────────────┐ <-- top of target
           │                    │
           │                    │
           │                    │
           │                    │
           │                    │
           │                    │
           │------┌──────┐------│
           │      │action│      │  <-- make sure we don't block click interactions here
           ├──────┤      ├──────┤  <-- bottom of target, top of hover extension
           │xxxxxx│      │xxxxxx│
           └──────┴──────┴──────┘  <-- bottom of hover extension

        * This helps if the user isn't 100% accurate in their mouse movements
        **/
        <div
          ref={hoverExtensionRef}
          className={styles.actionsHoverExtension}
          {...{ [SELECTABLE_OR_FRAME_ID_ATTR]: id }}
        />
      )}
      <div
        ref={contentRef}
        className={styles.actions}
        {...{ [SELECTABLE_OR_FRAME_ID_ATTR]: id }}
      >
        {content}
      </div>
    </>
  );
});
